/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import {
  IAny,
  IDetailComponentProps,
  IEmaasEnergyUsageComponentData,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataZone,
  IEnergyData,
  ISiteCard,
  ZoneWiseAggregations
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import EmaasEnergyUsagesAllUtilitySavingsByMeterDetail from './EmaasEnergyUsagesAllUtilitySavingsByMeterDetail';
import EmaasEnergyUsagesAllUtilitySavingsByMeterDetailNILM from './EmaasEnergyUsagesAllUtilitySavingsByMeterDetailNILM';
import { Card } from '@scuf/common';
import EmaasEnergyUsagesFloorWiseBreakDownMeter from './EmaasEnergyUsagesFloorWiseBreakDownMeter';
import { EnergyUsageUtilityType } from '../../../shared/Utils/Constants';
import {
  EmaasSubscriptionType,
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../shared/Enums';
import { ApiContext } from '../../../../context/ApiContext';

type IEnergyDataForZone =
  | IEmaasEnergyUsageComponentDataArea
  | IEmaasEnergyUsageComponentData
  | IEmaasEnergyUsageComponentDataZone;

type IAllEnergyTypeData =
  | IEmaasEnergyUsageComponentDataArea
  | IEnergyData
  | IEnergyDataForZone;

interface IEnergyDetails extends ISiteCard {
  data: IAllEnergyTypeData;
  subscriptionType: string;
  nilmTab?: string;
  ActiveIndex?: number;
}

const emaasEnergyUsagesAllUtilitySavingsByMeterDetil = (
  props: IEnergyDetails
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, subscriptionType, nilmTab } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }
  const context = useContext(ApiContext);
  const { dataSourceType } = context;
  // utility meter will only be set for premium subscription & feature flag on
  const isfloorWiseBreakdown =
    (subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION ||
      data.subscriptionType === EnergyUsageField.PREMIUMSUBS) &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;

  if (isfloorWiseBreakdown)
    return (
      <div>
        <EmaasEnergyUsagesFloorWiseBreakDownMeter
          data={data as IEmaasEnergyUsageComponentDataArea}
          ActiveIndex={props.ActiveIndex}
        />
      </div>
    );
  if (nilmTab) {
    return (
      <div>
        <EmaasEnergyUsagesAllUtilitySavingsByMeterDetailNILM
          data={data as IEnergyData}
        />
      </div>
    );
  } else
    return (
      <div>
        <EmaasEnergyUsagesAllUtilitySavingsByMeterDetail
          data={data as IEnergyData}
        />
      </div>
    );
};

const TotalEmissionsSavingsByMeterComponentWithLoader = withLoadingStatus(
  withErrorStatus(emaasEnergyUsagesAllUtilitySavingsByMeterDetil)
);

const EmaasEnergyUsagesAllUtilitySavingsByMeter = (
  props: IDetailComponentProps
) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const { dataConnector, subscriptionType, nilmTab, setHeader } = props;
  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalSavingsData = (all as unknown) as IAllEnergyTypeData;
  const isDataAvailable = carbonTotalSavingsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [subscriptionCheck, setSubscriptionCheck] = useState('Basic');
  const [data, setData] = useState({} as IAllEnergyTypeData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        const zoneWithMeterIds: IEnergyDataForZone = JSON.parse(
          JSON.stringify(carbonTotalSavingsData)
        );
        if (props.toggleSeparateApi) {
          zoneWithMeterIds.areaWiseAggregations =
            zoneWithMeterIds.areaWiseAggregations &&
            zoneWithMeterIds.areaWiseAggregations.map(
              (item: ZoneWiseAggregations) => {
                item.meterWiseAggregations = item.sortedMeterIds;
                return item;
              }
            );
        }
        const sortedZoneWithMeterIds: any = Object?.keys(zoneWithMeterIds)
          ?.length
          ? {
              ...zoneWithMeterIds,
              breakdownByMeter: zoneWithMeterIds?.breakdownByMeter?.sort(
                (a: any, b: any) => b.totalConsumption - a.totalConsumption
              )
            }
          : {};
        setData(sortedZoneWithMeterIds);
      }
      setErrorInfo(error);
      setStatusCode(status);
      setSubscriptionCheck(data.subscriptionType);
    }
  }, [isDataAvailable, loading, status]);
  const context = useContext(ApiContext);
  const { dataSourceType } = context;
  const isPremiumSubscription =
    (subscriptionType === EmaasSubscriptionType.PREMIUM_SUBSCRIPTION ||
      subscriptionCheck === EnergyUsageField.PREMIUMSUBS) &&
    dataSourceType !== EnergyUsageDataSourceType.UTILITY_METER;
  const breakDownByAreaHeader = isPremiumSubscription
    ? t(d.areasHeader)
    : t(d.breakdownCarbon);
  const breakDownBydefaultheader = isPremiumSubscription
    ? t(d.areasHeader)
    : t(d.ENERGYBREAKDOWNOFUTILITY);
  const header = nilmTab
    ? t(d.ENERGYBREAKDOWNBYMETERS)
    : props.UtilityBillType === EnergyUsageUtilityType.Electricity
    ? breakDownByAreaHeader
    : breakDownBydefaultheader;
  if (setHeader !== undefined) {
    setHeader(header);
  }

  return (
    <Card className="Savemeter">
      {/* <Card.Header className="emaas-carbon-view-details-total-emissions-savings-meter-card-header">
        {header}
      </Card.Header> */}
      <Card.Content>
        <div className="meter-savings-Datatable detail-view">
          <TotalEmissionsSavingsByMeterComponentWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            subscriptionType={subscriptionType!}
            nilmTab={nilmTab}
            ActiveIndex={props.ActiveIndex}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
export default EmaasEnergyUsagesAllUtilitySavingsByMeter;
